import { useState, useRef, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { ChevronDown, Plus, Building2, Check, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLAN_LABELS = {
  free: 'Free', basico: 'Básico', pro: 'Pro',
  enterprise: 'Empresarial', elite: 'Elite', five_stars: '5 Estrelas', livre: 'Livre'
};

export const CompanySelector = ({ onCompanySwitch }) => {
  const { user, companies, getToken } = useAuth();
  const [open, setOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [opening, setOpening] = useState(null);
  const [form, setForm] = useState({ company_name: '', company_slug: '', document: '' });
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (!companies || companies.length === 0) return null;

  const activeCompany = companies.find(c => c.id === user?.company_id);

  // Em vez de chamar switch-company (que tinha bugs de state mixing),
  // pedimos um magic link interno e fazemos hard-navigation pra ele.
  // Isso forca limpeza completa do estado + auto-login na nova loja sem riscos.
  const handleOpenStore = async (companyId) => {
    if (companyId === user?.company_id) { setOpen(false); return; }
    setOpening(companyId);
    try {
      const res = await axios.post(`${API}/auth/magic-link/internal`,
        { company_id: companyId, next: '/dashboard' },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      if (res.data?.url) {
        window.location.href = res.data.url;
        return;
      }
      toast.error('Nao foi possivel abrir esta loja');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao abrir loja');
    } finally {
      setOpening(null);
    }
  };

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const handleNameChange = (e) => {
    const name = e.target.value;
    setForm(prev => ({ ...prev, company_name: name, company_slug: generateSlug(name) }));
  };

  const handleAddCompany = async (e) => {
    e.preventDefault();
    if (!form.company_name.trim() || !form.company_slug.trim() || !form.document.trim()) {
      toast.error('Preencha todos os campos');
      return;
    }
    setCreating(true);
    try {
      const token = getToken();
      const res = await axios.post(`${API}/auth/add-company`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.token) {
        localStorage.setItem('token', res.data.token);
      }
      toast.success('Nova loja criada com sucesso!');
      setShowModal(false);
      setForm({ company_name: '', company_slug: '', document: '' });
      setOpen(false);
      if (onCompanySwitch) onCompanySwitch();
      window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao criar loja');
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <div className="relative" ref={ref} data-testid="company-selector">
        <button
          onClick={() => setOpen(!open)}
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 border border-white/10 hover:border-[#C5A059]/40 transition-all duration-200 text-left max-w-[220px] cursor-pointer"
          data-testid="company-selector-trigger"
        >
          <Building2 className="w-4 h-4 text-[#C5A059] shrink-0" />
          <span className="text-sm font-semibold text-white truncate">{activeCompany?.name || 'Selecionar'}</span>
          <ChevronDown className={`w-3.5 h-3.5 text-[#C5A059] shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
        </button>
        {open && (
          <div className="absolute top-full left-0 mt-1.5 w-64 bg-[#111827] border border-white/10 rounded-xl shadow-2xl z-50 overflow-hidden" data-testid="company-selector-dropdown">
            <div className="px-3 py-2 border-b border-white/5">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Suas Lojas</p>
            </div>
            <div className="max-h-[240px] overflow-y-auto">
              {companies.map(c => (
                <button
                  key={c.id}
                  onClick={() => handleOpenStore(c.id)}
                  disabled={opening === c.id}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-white/5 transition-colors disabled:opacity-60 ${c.id === user?.company_id ? 'bg-[#C5A059]/10' : ''}`}
                  data-testid={`company-option-${c.slug}`}
                >
                  {c.logo_url ? (
                    <img src={c.logo_url} alt="" className="w-8 h-8 rounded-lg object-cover bg-white/10" />
                  ) : (
                    <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
                      <Building2 className="w-4 h-4 text-slate-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">{c.name}</p>
                    <p className="text-[10px] text-slate-500">{PLAN_LABELS[c.plan] || c.plan}</p>
                  </div>
                  {opening === c.id ? (
                    <Loader2 className="w-4 h-4 text-[#C5A059] shrink-0 animate-spin" />
                  ) : c.id === user?.company_id ? (
                    <Check className="w-4 h-4 text-[#C5A059] shrink-0" />
                  ) : null}
                </button>
              ))}
            </div>
            <button
              onClick={() => { setOpen(false); setShowModal(true); }}
              className="w-full flex items-center gap-2 px-3 py-2.5 border-t border-white/5 text-[#C5A059] hover:bg-white/5 transition-colors text-sm font-medium cursor-pointer"
              data-testid="add-company-btn"
            >
              <Plus className="w-4 h-4" />Nova Loja
            </button>
          </div>
        )}
      </div>

      {/* Modal: Nova Loja */}
      {showModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center" data-testid="add-company-modal">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold text-slate-900">Adicionar Nova Loja</h2>
              <button onClick={() => setShowModal(false)} className="p-1 rounded-lg hover:bg-slate-100 transition-colors" data-testid="close-add-company-modal">
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>
            <form onSubmit={handleAddCompany} className="space-y-4">
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Nome da Empresa</Label>
                <Input
                  value={form.company_name}
                  onChange={handleNameChange}
                  placeholder="Ex: Padaria do Centro"
                  className="h-11"
                  data-testid="new-company-name"
                  required
                />
              </div>
              <div>
                <Label className="text-sm font-medium mb-1.5 block">URL da Loja</Label>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500 whitespace-nowrap">falocomodono.com.br/</span>
                  <Input
                    value={form.company_slug}
                    onChange={(e) => setForm(prev => ({ ...prev, company_slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))}
                    placeholder="padaria-do-centro"
                    className="h-11 flex-1"
                    data-testid="new-company-slug"
                    required
                  />
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium mb-1.5 block">CNPJ / CPF</Label>
                <Input
                  value={form.document}
                  onChange={(e) => setForm(prev => ({ ...prev, document: e.target.value }))}
                  placeholder="00.000.000/0000-00"
                  className="h-11"
                  data-testid="new-company-document"
                  required
                />
              </div>
              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" className="flex-1 h-11" onClick={() => setShowModal(false)}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={creating} className="flex-1 h-11 bg-[#0A2647] hover:bg-[#0A2647]/90 text-white" data-testid="submit-new-company">
                  {creating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Criando...</> : 'Criar Loja'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};
